import React, { useEffect, useRef, useState } from 'react';
import api, { mediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit, X, Save, Image as ImageIcon, Loader2, GripVertical, Eye, EyeOff, Tv, Zap, Sparkles, Film, Radio, Cast, MonitorPlay, Play, ArrowUp, ArrowDown } from 'lucide-react';

const ICONS = ['Tv', 'Zap', 'Sparkles', 'Film', 'Radio', 'Cast', 'MonitorPlay', 'Play'];
const ICON_MAP = { Tv, Zap, Sparkles, Film, Radio, Cast, MonitorPlay, Play };

export default function AdminApps() {
  const [apps, setApps] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const token = localStorage.getItem('gtc_token');

  const load = async () => {
    try {
      const { data } = await api.get('/download-apps');
      setApps(data);
    } catch { toast.error('Errore caricamento'); }
  };

  useEffect(() => { load(); }, []);

  const persistOrder = async (list) => {
    try {
      await api.put('/download-apps/reorder', { ids: list.map((a) => a.id) });
    } catch { toast.error('Errore riordinamento'); load(); }
  };

  const reorder = (from, to) => {
    if (from === to || to < 0 || to >= apps.length) return;
    const next = [...apps];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setApps(next);
    persistOrder(next);
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => { if (dragIdx !== null) reorder(dragIdx, idx); setDragIdx(null); };

  const toggleActive = async (app) => {
    try {
      await api.put(`/download-apps/${app.id}`, { is_active: !app.is_active });
      load();
    } catch { toast.error('Errore'); }
  };
  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/download-apps/${deleteTarget.id}`);
      toast.success('App eliminata');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4">
      <button
        data-testid="admin-new-app-btn"
        onClick={() => { setEditing(null); setShowEditor(true); }}
        className="btn-gold w-full py-3 flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Nuova applicazione
      </button>

      <div className="space-y-3">
        {apps.length === 0 && <div className="text-center text-white/40 text-sm py-6">Nessuna app configurata.</div>}
        {apps.map((app, idx) => {
          const Icon = ICON_MAP[app.icon_name] || Tv;
          return (
            <div
              key={app.id}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              onDragEnd={() => setDragIdx(null)}
              className={`card-surface p-3 flex items-center gap-2 ${!app.is_active ? 'opacity-50' : ''} ${dragIdx === idx ? 'ring-2 ring-[var(--gold)]/60' : ''} cursor-move`}
              data-testid={`admin-app-${app.id}`}
            >
              <div className="flex flex-col gap-0.5 text-white/40 hover:text-[var(--gold)]">
                <button data-testid={`app-up-${app.id}`} onClick={() => reorder(idx, idx - 1)} disabled={idx === 0} className="disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                <GripVertical className="w-3.5 h-3.5 opacity-40" />
                <button data-testid={`app-down-${app.id}`} onClick={() => reorder(idx, idx + 1)} disabled={idx === apps.length - 1} className="disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[var(--bg-elevated)] border border-[var(--gold)]/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {app.logo_url
                  ? <img src={mediaUrl(app.logo_url)} alt="" className="w-full h-full object-cover" />
                  : <Icon className="w-6 h-6 text-[var(--gold)]" strokeWidth={1.5} />
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-medium text-white text-sm truncate">{app.name}</div>
                {app.tagline && <div className="text-[11px] text-white/50 truncate">{app.tagline}</div>}
                {app.url && <div className="text-[10px] text-[var(--gold)]/70 truncate mt-0.5">{app.url}</div>}
              </div>
              <button data-testid={`app-toggle-${app.id}`} onClick={() => toggleActive(app)} className="p-1.5 text-white/60 hover:text-[var(--gold)]" title={app.is_active ? 'Nascondi' : 'Mostra'}>
                {app.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button data-testid={`app-edit-${app.id}`} onClick={() => { setEditing(app); setShowEditor(true); }} className="p-1.5 text-white/60 hover:text-[var(--gold)]"><Edit className="w-4 h-4" /></button>
              <button data-testid={`app-delete-${app.id}`} onClick={() => setDeleteTarget(app)} className="p-1.5 text-white/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {showEditor && (
          <AppEditor initial={editing} onClose={() => setShowEditor(false)} onSaved={() => { setShowEditor(false); load(); }} />
        )}
        {deleteTarget && (
          <Overlay onClose={() => setDeleteTarget(null)}>
            <h3 className="font-heading text-lg">Elimina app</h3>
            <p className="text-sm text-white/70">Eliminare definitivamente <span className="text-[var(--gold)] font-semibold">"{deleteTarget.name}"</span>?</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-3 rounded-full border border-[var(--border-subtle)] text-white/80 text-sm">Annulla</button>
              <button data-testid="app-delete-confirm" onClick={doDelete} className="flex-1 py-3 rounded-full bg-red-500 text-white font-semibold text-sm">Elimina</button>
            </div>
          </Overlay>
        )}
      </AnimatePresence>
    </div>
  );
}

function AppEditor({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || '');
  const [tagline, setTagline] = useState(initial?.tagline || '');
  const [url, setUrl] = useState(initial?.url || '');
  const [iconName, setIconName] = useState(initial?.icon_name || 'Tv');
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('gtc_token');

  const submit = async () => {
    if (!name.trim()) return toast.error('Nome obbligatorio');
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/download-apps/${initial.id}`, { name, tagline, url, icon_name: iconName });
        if (logo) {
          const form = new FormData();
          form.append('logo', logo);
          await api.put(`/download-apps/${initial.id}/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        toast.success('App aggiornata');
      } else {
        const form = new FormData();
        form.append('name', name);
        form.append('tagline', tagline);
        form.append('url', url);
        form.append('icon_name', iconName);
        if (logo) form.append('logo', logo);
        await api.post('/download-apps', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('App creata');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  const previewLogoUrl = logo ? URL.createObjectURL(logo) : (initial?.logo_url ? mediaUrl(initial.logo_url) : null);
  const SelectedIcon = ICON_MAP[iconName] || Tv;

  return (
    <Overlay onClose={onClose} maxWidth="max-w-md">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-medium">{isEdit ? 'Modifica app' : 'Nuova app'}</h3>
        <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-4 h-4" /></button>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Nome</div>
        <input data-testid="app-name-input" className="input-gold" placeholder="Es. Golden TV 3.0" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Descrizione (opzionale)</div>
        <input data-testid="app-tagline-input" className="input-gold" placeholder="Es. La nostra app principale…" value={tagline} onChange={(e) => setTagline(e.target.value)} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Link di download</div>
        <input data-testid="app-url-input" className="input-gold" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Logo (opzionale)</div>
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--gold)]/30 flex items-center justify-center overflow-hidden flex-shrink-0">
            {previewLogoUrl
              ? <img src={previewLogoUrl} alt="" className="w-full h-full object-cover" />
              : <SelectedIcon className="w-6 h-6 text-[var(--gold)]" />
            }
          </div>
          <button
            data-testid="app-logo-btn"
            onClick={() => fileRef.current?.click()}
            className="flex-1 py-3 rounded-2xl border border-dashed border-[var(--border-subtle)] hover:border-[var(--gold)]/50 text-sm text-white/60 flex items-center justify-center gap-2"
          >
            <ImageIcon className="w-4 h-4" />
            {logo ? logo.name : 'Carica logo…'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setLogo(e.target.files?.[0] || null)} />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Icona di default (usata se non carichi il logo)</div>
        <div className="flex flex-wrap gap-2">
          {ICONS.map((n) => {
            const I = ICON_MAP[n];
            return (
              <button
                key={n}
                data-testid={`app-icon-${n}`}
                onClick={() => setIconName(n)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-colors ${
                  iconName === n ? 'bg-[var(--gold-soft)] border-[var(--gold)]/60 text-[var(--gold)]' : 'bg-[var(--bg-surface)] border-[var(--border-subtle)] text-white/60'
                }`}
              >
                <I className="w-5 h-5" strokeWidth={1.6} />
              </button>
            );
          })}
        </div>
      </div>

      <button data-testid="app-editor-submit" onClick={submit} disabled={saving} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {isEdit ? 'Salva modifiche' : 'Crea app'}</>}
      </button>
    </Overlay>
  );
}

function Overlay({ children, onClose, maxWidth = 'max-w-sm' }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 40, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className={`card-surface p-5 w-full ${maxWidth} space-y-4 my-8`}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
